import React, { useState, useRef, useEffect } from "react";
import { GrantsLabelItem } from "../../../services/grantsApi";
import { LabelCount } from "../../../types/grants";
import { LABEL_COLORS, getColorConfig } from "./labelColors";
import { getSafeErrorMessage, MAX_LABELS } from "./useCallLabels";
import { useFocusTrap } from "../../../hooks/useFocusTrap";
import { useToast } from "../../../contexts/ToastContext";

interface CallLabelManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  labels: GrantsLabelItem[];
  /** Label counts from the API response, used for delete confirmation */
  labelCounts?: LabelCount[];
  onUpdateLabel: (
    id: string,
    payload: { name?: string; color?: string }
  ) => Promise<unknown>;
  onDeleteLabel: (id: string) => Promise<void>;
  onCreateLabel: (name: string, color: string) => Promise<unknown>;
}

/**
 * Modal for managing labels (rename, recolor, delete).
 *
 * Features:
 * - List of labels with color dot, editable name, color palette, delete button
 * - Delete confirmation with count of assigned calls
 * - Counter "N / 10 etykiet"
 * - Create new label (with color picker)
 */
const CallLabelManagementModal: React.FC<CallLabelManagementModalProps> = ({
  isOpen,
  onClose,
  labels,
  labelCounts = [],
  onUpdateLabel,
  onDeleteLabel,
  onCreateLabel,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();
  useFocusTrap(modalRef, isOpen);

  // Editing state: which label is being edited
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Delete confirmation state
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Create new label state
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<string>(LABEL_COLORS[0].key);
  const [createError, setCreateError] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const newNameInputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEditingId(null);
      setEditName("");
      setEditColor("");
      setEditError(null);
      setConfirmDeleteId(null);
      setIsCreating(false);
      setNewName("");
      setCreateError(null);
    }
  }, [isOpen]);

  // Auto-focus edit input
  useEffect(() => {
    if (editingId && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [editingId]);

  // Auto-focus create input
  useEffect(() => {
    if (isCreating && newNameInputRef.current) {
      newNameInputRef.current.focus();
    }
  }, [isCreating]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getAssignmentCount = (labelId: string): number => {
    const found = labelCounts.find((lc) => lc.id === labelId);
    return found?.count ?? 0;
  };

  const startEdit = (label: GrantsLabelItem) => {
    setEditingId(label.id);
    setEditName(label.name);
    setEditColor(label.color);
    setEditError(null);
    setConfirmDeleteId(null);
    setIsCreating(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditColor("");
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const trimmedName = editName.trim();
    if (!trimmedName) {
      setEditError("Nazwa etykiety jest wymagana");
      return;
    }

    // Check for duplicate name (case-insensitive, excluding self)
    const duplicate = labels.find(
      (l) =>
        l.id !== editingId &&
        l.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (duplicate) {
      setEditError("Etykieta o tej nazwie już istnieje");
      return;
    }

    setSaving(true);
    setEditError(null);
    try {
      await onUpdateLabel(editingId, {
        name: trimmedName,
        color: editColor,
      });
      setEditingId(null);
    } catch (err) {
      setEditError(getSafeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      await onDeleteLabel(id);
      setConfirmDeleteId(null);
      addToast({ type: "success", message: "Etykieta została usunięta" });
    } catch (err) {
      addToast({ type: "error", message: getSafeErrorMessage(err) });
    } finally {
      setDeleting(false);
    }
  };

  const handleCreate = async () => {
    const trimmedName = newName.trim();
    if (!trimmedName) {
      setCreateError("Nazwa etykiety jest wymagana");
      return;
    }

    const duplicate = labels.find(
      (l) => l.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (duplicate) {
      setCreateError("Etykieta o tej nazwie już istnieje");
      return;
    }

    setSaving(true);
    setCreateError(null);
    try {
      await onCreateLabel(trimmedName, newColor);
      setIsCreating(false);
      setNewName("");
      setNewColor(LABEL_COLORS[0].key);
    } catch (err) {
      setCreateError(getSafeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const sortedLabels = [...labels].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );
  const isAtLimit = labels.length >= MAX_LABELS;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Zarządzaj etykietami"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              Zarządzaj etykietami
            </h2>
            <p className="text-sm text-neutral-500 mt-0.5">
              {labels.length} / {MAX_LABELS} etykiet
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
            aria-label="Zamknij"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {sortedLabels.length === 0 && !isCreating && (
            <p className="text-sm text-neutral-500 py-4 text-center">
              Nie masz jeszcze etykiet. Utwórz pierwszą!
            </p>
          )}

          {sortedLabels.map((label) => {
            const colorCfg = getColorConfig(label.color);
            const isEditing = editingId === label.id;
            const isConfirmingDelete = confirmDeleteId === label.id;
            const count = getAssignmentCount(label.id);

            if (isEditing) {
              return (
                <div
                  key={label.id}
                  className="border border-primary-200 rounded-md p-3 space-y-2 bg-primary-50"
                >
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={editName}
                    onChange={(e) => {
                      setEditName(e.target.value);
                      setEditError(null);
                    }}
                    placeholder="Nazwa etykiety"
                    maxLength={50}
                    disabled={saving}
                    className="w-full text-sm border border-neutral-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveEdit();
                      if (e.key === "Escape") cancelEdit();
                    }}
                  />

                  {/* Color palette */}
                  <div
                    className="flex flex-wrap gap-1.5"
                    role="radiogroup"
                    aria-label="Wybierz kolor etykiety"
                  >
                    {LABEL_COLORS.map((color) => (
                      <button
                        key={color.key}
                        onClick={() => setEditColor(color.key)}
                        role="radio"
                        aria-checked={editColor === color.key}
                        aria-label={`Wybierz kolor ${color.name}`}
                        disabled={saving}
                        className={`w-6 h-6 rounded-full ${color.dot} ${
                          editColor === color.key
                            ? "ring-2 ring-offset-1 ring-primary-500"
                            : ""
                        } disabled:opacity-50`}
                        title={color.name}
                      />
                    ))}
                  </div>

                  {editError && (
                    <p className="text-xs text-red-600" role="alert">
                      {editError}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveEdit}
                      disabled={saving}
                      className="text-xs bg-primary-600 text-white px-3 py-1 rounded hover:bg-primary-700 disabled:opacity-50"
                    >
                      {saving ? "Zapisuję..." : "Zapisz"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={saving}
                      className="text-xs text-neutral-600 px-3 py-1 rounded hover:bg-neutral-200"
                    >
                      Anuluj
                    </button>
                  </div>
                </div>
              );
            }

            if (isConfirmingDelete) {
              return (
                <div
                  key={label.id}
                  className="border border-red-200 rounded-md p-3 bg-red-50"
                >
                  <p className="text-sm text-red-800 mb-2">
                    {count > 0
                      ? `Ta etykieta jest przypisana do ${count} naborów. Usunąć?`
                      : "Usunąć tę etykietę?"}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDelete(label.id)}
                      disabled={deleting}
                      className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      {deleting ? "Usuwam..." : "Tak, usuń"}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      disabled={deleting}
                      className="text-xs text-neutral-600 px-3 py-1 rounded hover:bg-neutral-200"
                    >
                      Anuluj
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={label.id}
                className="flex items-center justify-between py-2 px-2 rounded hover:bg-neutral-50"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-3 h-3 rounded-full shrink-0 ${colorCfg.dot}`}
                  />
                  <span className="text-sm text-neutral-900 truncate">
                    {label.name}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => startEdit(label)}
                    aria-label={`Edytuj etykietę ${label.name}`}
                    className="text-xs text-neutral-500 hover:text-neutral-700 px-2 py-1 rounded hover:bg-neutral-100"
                  >
                    Edytuj
                  </button>
                  <button
                    onClick={() => {
                      setConfirmDeleteId(label.id);
                      setEditingId(null);
                    }}
                    aria-label={`Usuń etykietę ${label.name}`}
                    className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
                  >
                    Usuń
                  </button>
                </div>
              </div>
            );
          })}

          {/* Create new label form */}
          {isCreating && (
            <div className="border border-neutral-200 rounded-md p-3 space-y-2 bg-neutral-50">
              <input
                ref={newNameInputRef}
                type="text"
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  setCreateError(null);
                }}
                placeholder="Nazwa nowej etykiety"
                maxLength={50}
                disabled={saving}
                className="w-full text-sm border border-neutral-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                  if (e.key === "Escape") {
                    setIsCreating(false);
                    setNewName("");
                    setCreateError(null);
                  }
                }}
              />

              {/* Color palette */}
              <div
                className="flex flex-wrap gap-1.5"
                role="radiogroup"
                aria-label="Wybierz kolor nowej etykiety"
              >
                {LABEL_COLORS.map((color) => (
                  <button
                    key={color.key}
                    onClick={() => setNewColor(color.key)}
                    role="radio"
                    aria-checked={newColor === color.key}
                    aria-label={`Wybierz kolor ${color.name}`}
                    disabled={saving}
                    className={`w-6 h-6 rounded-full ${color.dot} ${
                      newColor === color.key
                        ? "ring-2 ring-offset-1 ring-primary-500"
                        : ""
                    } disabled:opacity-50`}
                    title={color.name}
                  />
                ))}
              </div>

              <p className="text-xs text-neutral-500">
                Enter = zapisz, Escape = anuluj
              </p>

              {createError && (
                <p className="text-xs text-red-600" role="alert">
                  {createError}
                </p>
              )}

              <div className="flex gap-2">
                <button
                  onClick={handleCreate}
                  disabled={saving}
                  className="text-xs bg-primary-600 text-white px-3 py-1 rounded hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? "Tworzę..." : "Utwórz"}
                </button>
                <button
                  onClick={() => {
                    setIsCreating(false);
                    setNewName("");
                    setCreateError(null);
                  }}
                  className="text-xs text-neutral-600 px-3 py-1 rounded hover:bg-neutral-200"
                >
                  Anuluj
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between">
          {!isCreating && (
            <button
              onClick={() => {
                setIsCreating(true);
                setEditingId(null);
                setConfirmDeleteId(null);
              }}
              disabled={isAtLimit}
              aria-disabled={isAtLimit}
              title={isAtLimit ? "Osiągnięty limit 10 etykiet" : "Dodaj nową etykietę"}
              className={`text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 rounded px-3 py-1.5 ${
                isAtLimit
                  ? "text-neutral-400 cursor-not-allowed"
                  : "text-primary-600 hover:bg-primary-50"
              }`}
            >
              + Dodaj etykietę
            </button>
          )}
          <button
            onClick={onClose}
            className="text-sm text-neutral-600 hover:text-neutral-800 px-3 py-1.5 rounded hover:bg-neutral-100"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallLabelManagementModal;
